import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ScopeSelector } from '@workspace/ui/src/components/scope-selector';

import { NetworkMap } from './features/network/NetworkMap';
import { createPublicStatusTelemetryClient } from './features/network/statusTelemetryApi';
import { publicStatusTenantSlugFromPath } from './publicStatusRoute';
import { claimStatusReauthentication, STATUS_SESSION_EXPIRED_EVENT } from './statusSessionExpiry';
import { buildStatusLoginUrl, statusTenantSlugFromSearch } from './tenantStatusRoute';

type AuthState = 'checking' | 'ready' | 'error';
type ScopeTenant = {
  id: string;
  name?: string | null;
  slug?: string | null;
  status?: string | null;
  accountId?: string | null;
  organizationId?: string | null;
};

type ScopeContextResponse = {
  mode?: 'global' | 'tenant';
  allowGlobal?: boolean;
  activeTenant?: ScopeTenant | null;
};

type ScopeTenantList = {
  data?: ScopeTenant[];
};

const withKioskQuery = (rawUrl: string) => {
  const url = new URL(rawUrl);
  url.searchParams.set('kiosk', '1');
  return url.toString();
};

const PublicStatusWorkspace = ({ tenantSlug }: { tenantSlug: string }) => {
  const client = useMemo(() => createPublicStatusTelemetryClient(tenantSlug), [tenantSlug]);
  return (
    <main className="relative flex-1 overflow-hidden">
      <div className="pointer-events-none absolute bottom-4 right-4 z-20 max-w-xs rounded-md border border-border/60 bg-background/95 px-3 py-2 text-[10px] text-muted-foreground shadow-lg backdrop-blur">
        Public view · approximate locations · operational details withheld
      </div>
      <NetworkMap
        showHealthy
        showBroken
        publicMode
        telemetryClient={client}
        snapshotPollIntervalMs={30_000}
      />
    </main>
  );
};

const EmptyTenantState = () => (
  <div className="flex h-full items-center justify-center px-6">
    <div className="max-w-md rounded-xl border border-border/70 bg-background/90 p-6 text-center shadow-sm backdrop-blur">
      <h1 className="text-lg font-semibold text-foreground">Select a tenant</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Choose the tenant you want to monitor. The map stays pinned to that tenant until you switch it.
      </p>
    </div>
  </div>
);

const ScopeErrorState = ({ message }: { message: string }) => (
  <div className="flex h-full items-center justify-center px-6">
    <div className="max-w-md rounded-xl border border-border/70 bg-background/90 p-6 text-center shadow-sm backdrop-blur">
      <h1 className="text-lg font-semibold text-foreground">Unable to load tenant scope</h1>
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
    </div>
  </div>
);

const fetchJSON = async <T,>(url: string): Promise<T> => {
  const response = await fetch(url, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }
  return (await response.json()) as T;
};

const postJSON = async (url: string, body: unknown): Promise<void> => {
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }
};

const StatusWorkspace = ({
  kioskMode,
  isDebugMap,
  kioskRedirecting,
  requestedTenantSlug,
  onSwitchToKioskMode
}: {
  kioskMode: boolean;
  isDebugMap: boolean;
  kioskRedirecting: boolean;
  requestedTenantSlug: string | null;
  onSwitchToKioskMode: () => void;
}) => {
  const [scope, setScope] = useState<ScopeContextResponse | null>(null);
  const [tenants, setTenants] = useState<ScopeTenant[]>([]);
  const [scopeLoading, setScopeLoading] = useState(true);
  const [scopeError, setScopeError] = useState<string | null>(null);
  const requestedTenantApplied = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const loadScope = async () => {
      setScopeLoading(true);
      setScopeError(null);
      try {
        const [scopeContext, tenantOptions] = await Promise.all([
          fetchJSON<ScopeContextResponse>('/api/v1/scope'),
          fetchJSON<ScopeTenantList>('/api/v1/scope/tenants?limit=100')
        ]);
        if (cancelled) {
          return;
        }
        setScope(scopeContext);
        setTenants(tenantOptions.data ?? []);
      } catch (error) {
        if (cancelled) {
          return;
        }
        setScopeError(error instanceof Error ? error.message : 'Unable to load tenant scope');
      } finally {
        if (!cancelled) {
          setScopeLoading(false);
        }
      }
    };

    void loadScope();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelectTenant = useCallback(async (tenantId: string) => {
    try {
      setScopeLoading(true);
      setScopeError(null);
      await postJSON('/api/v1/scope/select', { tenantId });
      const scopeContext = await fetchJSON<ScopeContextResponse>('/api/v1/scope');
      setScope(scopeContext);
    } catch (error) {
      setScopeError(error instanceof Error ? error.message : 'Unable to switch tenant');
    } finally {
      setScopeLoading(false);
    }
  }, []);

  useEffect(() => {
    if (requestedTenantApplied.current || scopeLoading || !requestedTenantSlug) return;
    if (scope?.activeTenant?.slug === requestedTenantSlug) {
      requestedTenantApplied.current = true;
      return;
    }
    const requestedTenant = tenants.find(tenant => tenant.slug === requestedTenantSlug);
    if (!requestedTenant) return;
    requestedTenantApplied.current = true;
    void handleSelectTenant(requestedTenant.id);
  }, [handleSelectTenant, requestedTenantSlug, scope?.activeTenant?.slug, scopeLoading, tenants]);

  const selector = (
    <div className="pointer-events-auto absolute right-4 top-4 z-20 flex flex-col items-end gap-2 md:top-6">
      <ScopeSelector
        mode="tenant"
        allowGlobal={false}
        activeTenant={scope?.activeTenant ?? undefined}
        tenants={tenants}
        loading={scopeLoading}
        variant="tenant"
        onSelectTenant={handleSelectTenant}
      />
      {!kioskMode ? (
        <button
          type="button"
          onClick={onSwitchToKioskMode}
          disabled={kioskRedirecting}
          className="inline-flex items-center rounded-md border border-border/70 bg-background/90 px-3 py-2 text-xs font-medium text-foreground shadow-sm backdrop-blur hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {kioskRedirecting ? 'Switching…' : 'Switch to Kiosk Mode'}
        </button>
      ) : (
        <div className="inline-flex items-center rounded-md border border-primary/50 bg-primary/10 px-3 py-2 text-xs font-medium text-primary shadow-sm backdrop-blur">
          Kiosk mode enabled
        </div>
      )}
    </div>
  );

  if (scopeError) {
    return (
      <main className="relative flex-1 overflow-hidden">
        {selector}
        <ScopeErrorState message={scopeError} />
      </main>
    );
  }

  if (!scopeLoading && !scope?.activeTenant?.id) {
    return (
      <main className="relative flex-1 overflow-hidden">
        {selector}
        <EmptyTenantState />
      </main>
    );
  }

  return (
    <main className="relative flex-1 overflow-hidden">
      {selector}
      <NetworkMap
        key={scope?.activeTenant?.id ?? 'no-tenant'}
        showHealthy
        showBroken
        staticMode={isDebugMap}
        kioskMode={kioskMode}
      />
    </main>
  );
};

export const App = () => {
  const [authState, setAuthState] = useState<AuthState>('checking');
  const [kioskRedirecting, setKioskRedirecting] = useState(false);
  const sessionRedirecting = useRef(false);
  const kioskMode =
    typeof window !== 'undefined' && new URL(window.location.href).searchParams.get('kiosk') === '1';
  const isDebugMap =
    typeof window !== 'undefined' &&
    window.location.pathname.replace(/\/+$/, '') === '/debugmap';
  const publicTenantSlug =
    typeof window === 'undefined' ? null : publicStatusTenantSlugFromPath(window.location.pathname);
  const requestedTenantSelector =
    typeof window === 'undefined' ? null : statusTenantSlugFromSearch(window.location.search);
  const invalidTenantSelector = requestedTenantSelector === undefined;
  const requestedTenantSlug = typeof requestedTenantSelector === 'string' ? requestedTenantSelector : null;

  const restartLogin = useCallback(() => {
    if (
      typeof window === 'undefined' ||
      publicTenantSlug ||
      invalidTenantSelector ||
      sessionRedirecting.current
    ) {
      return;
    }
    if (!claimStatusReauthentication(window.location.href)) {
      setAuthState('error');
      return;
    }
    sessionRedirecting.current = true;
    window.location.href = buildStatusLoginUrl(window.location.href, {
      kiosk: kioskMode,
      tenant: requestedTenantSlug
    });
  }, [invalidTenantSelector, kioskMode, publicTenantSlug, requestedTenantSlug]);

  useEffect(() => {
    window.addEventListener(STATUS_SESSION_EXPIRED_EVENT, restartLogin);
    return () => window.removeEventListener(STATUS_SESSION_EXPIRED_EVENT, restartLogin);
  }, [restartLogin]);

  useEffect(() => {
    let cancelled = false;
    const verifySession = async () => {
      if (publicTenantSlug || invalidTenantSelector) return;
      try {
        const response = await fetch('/api/v1/auth/session', {
          method: 'GET',
          credentials: 'include'
        });

        if (response.status === 401) {
          restartLogin();
          return;
        }

        if (!response.ok) {
          throw new Error(`session: ${response.status}`);
        }

        if (!cancelled) {
          setAuthState('ready');
        }
      } catch {
        if (!cancelled) {
          setAuthState('error');
        }
      }
    };

    void verifySession();
    return () => {
      cancelled = true;
    };
  }, [invalidTenantSelector, publicTenantSlug, restartLogin]);

  useEffect(() => {
    if (!kioskMode || publicTenantSlug) {
      return;
    }

    const heartbeat = async () => {
      try {
        const response = await fetch('/api/v1/auth/session', {
          method: 'GET',
          credentials: 'include'
        });
        if (response.status === 401 && typeof window !== 'undefined') {
          restartLogin();
        }
      } catch {
        // Heartbeat failures are non-fatal; the next user-visible request will surface auth issues.
      }
    };

    const intervalId = window.setInterval(() => {
      void heartbeat();
    }, 5 * 60 * 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [kioskMode, publicTenantSlug, restartLogin]);

  if (publicTenantSlug) {
    return (
      <div className="flex h-[100svh] w-screen flex-col overflow-hidden bg-background text-foreground">
        <PublicStatusWorkspace tenantSlug={publicTenantSlug} />
      </div>
    );
  }

  if (invalidTenantSelector) {
    return (
      <div className="flex h-[100svh] w-screen items-center justify-center bg-background px-6 text-foreground">
        <div className="max-w-md text-center text-sm text-muted-foreground">
          Invalid tenant selector. Use one lower-case tenant name in the status link.
        </div>
      </div>
    );
  }

  if (authState === 'checking') {
    return (
      <div className="flex h-[100svh] w-screen items-center justify-center bg-background text-foreground">
        <div className="text-sm text-muted-foreground">Checking session…</div>
      </div>
    );
  }

  if (authState === 'error') {
    return (
      <div className="flex h-[100svh] w-screen items-center justify-center bg-background text-foreground">
        <div className="text-sm text-muted-foreground">Unable to verify session.</div>
      </div>
    );
  }

  const handleSwitchToKioskMode = () => {
    if (typeof window === 'undefined') {
      return;
    }
    setKioskRedirecting(true);
    const returnUrl = withKioskQuery(window.location.href);
    window.location.href = buildStatusLoginUrl(returnUrl, {
      kiosk: true,
      tenant: requestedTenantSlug
    });
  };

  return (
    <div className="flex h-[100svh] w-screen flex-col overflow-hidden bg-background text-foreground">
      <StatusWorkspace
        kioskMode={kioskMode}
        isDebugMap={isDebugMap}
        kioskRedirecting={kioskRedirecting}
        requestedTenantSlug={requestedTenantSlug}
        onSwitchToKioskMode={handleSwitchToKioskMode}
      />
    </div>
  );
};
